import { type JSX, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { Icon } from '@/components/icons';
import {
  Button,
  Card,
  DateRangePicker,
  Skeleton,
  type DateRangePreset,
} from '@/components/ui';
import {
  downloadReportCSV,
  useReportBreakdownByCampaign,
  useReportBreakdownBySender,
  useReportSummary,
  useReportVolume,
  type ReportRange,
} from '@/hooks/useReports';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

type PresetKey = '7d' | '30d' | '90d' | 'this_month' | 'last_month';

// Preset ranges anchored to the current wall-clock time. Recomputed on
// every render so picking "Last 30 days" tomorrow yields a different
// window without a manual refresh. UTC throughout — the backend stores
// timestamps in UTC and the chart axis re-localises to the browser TZ.
function makePresets(now: Date = new Date()): Record<PresetKey, ReportRange> {
  const dayMs = 24 * 60 * 60 * 1000;
  const toIso = (d: Date) => d.toISOString();
  const startOfDay = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const startOfMonth = (d: Date) => new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1));

  const today = startOfDay(now);
  const thisMonthStart = startOfMonth(now);
  const lastMonthStart = startOfMonth(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1)));

  return {
    '7d': { from: toIso(new Date(today.getTime() - 7 * dayMs)), to: toIso(now) },
    '30d': { from: toIso(new Date(today.getTime() - 30 * dayMs)), to: toIso(now) },
    '90d': { from: toIso(new Date(today.getTime() - 90 * dayMs)), to: toIso(now) },
    this_month: { from: toIso(thisMonthStart), to: toIso(now) },
    last_month: { from: toIso(lastMonthStart), to: toIso(thisMonthStart) },
  };
}

// ---------------------------------------------------------------------------
// KPI card with delta (vs previous equally-sized window).
// ---------------------------------------------------------------------------
interface KpiProps {
  label: string;
  value: string;
  delta?: number | null;
  deltaInverted?: boolean; // for "Failed" — going down is good
  loading?: boolean;
}

function Kpi({ label, value, delta, deltaInverted, loading }: KpiProps): JSX.Element {
  const positive = delta != null && delta >= 0;
  const good = deltaInverted ? !positive : positive;
  return (
    <Card padding="md">
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
        {label}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-24 mt-2" />
      ) : (
        <div className="num display text-[26px] font-semibold text-text mt-1.5 tracking-tight leading-none">
          {value}
        </div>
      )}
      <div className="min-h-[20px] mt-2">
        {!loading && delta != null && Number.isFinite(delta) && (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[12px] font-medium',
              good ? 'text-success' : 'text-danger',
            )}
          >
            {positive ? (
              <Icon.arrowUp width="12" height="12" />
            ) : (
              <Icon.arrowDown width="12" height="12" />
            )}
            <span className="num">
              {positive ? '+' : ''}
              {(delta * 100).toFixed(1)}%
            </span>
          </span>
        )}
      </div>
    </Card>
  );
}

// (current - previous) / previous, returning null when previous=0 to avoid
// reporting "+∞%" the first time a counter exits zero.
function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return (current - previous) / previous;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
export default function ReportsPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const presets = useMemo(() => makePresets(), []);

  const [presetKey, setPresetKey] = useState<string>('30d');
  const [customRange, setCustomRange] = useState<ReportRange>(presets['30d']);
  const range: ReportRange =
    presetKey === 'custom' ? customRange : presets[presetKey as PresetKey];

  const summary = useReportSummary(range);
  const volume = useReportVolume(range);
  const senders = useReportBreakdownBySender(range, 5);
  const campaigns = useReportBreakdownByCampaign(range, 5);

  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = async () => {
    setExporting(true);
    setExportError(null);
    try {
      await downloadReportCSV(range);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const cur = summary.data?.current;
  const prev = summary.data?.previous;
  const hasCompare = summary.data?.has_compare ?? false;

  const totalDelta = hasCompare && cur && prev ? pctChange(cur.total, prev.total) : null;
  const deliveredDelta =
    hasCompare && cur && prev ? pctChange(cur.delivered, prev.delivered) : null;
  const failedDelta = hasCompare && cur && prev ? pctChange(cur.failed, prev.failed) : null;
  const spendDelta = hasCompare && cur && prev ? pctChange(cur.spend, prev.spend) : null;
  const inboundDelta = hasCompare && cur && prev ? pctChange(cur.inbound, prev.inbound) : null;

  const volumeRows = useMemo(() => {
    return (volume.data?.items ?? []).map((p) => ({
      day: p.day,
      label: new Date(`${p.day}T00:00:00Z`).toLocaleDateString(lang, {
        day: 'numeric',
        month: 'short',
      }),
      total: p.total,
      delivered: p.delivered,
      failed: p.failed,
    }));
  }, [volume.data, lang]);

  const presetLabel: Record<PresetKey, string> = {
    '7d': t('reports_preset_7d'),
    '30d': t('reports_preset_30d'),
    '90d': t('reports_preset_90d'),
    this_month: t('reports_preset_this_month'),
    last_month: t('reports_preset_last_month'),
  };

  const pickerPresets: DateRangePreset[] = (Object.keys(presets) as PresetKey[]).map(
    (key) => ({ key, label: presetLabel[key], range: presets[key] }),
  );

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {t('nav_reports')}
          </h1>
          <p className="text-[13px] text-text-3 mt-1.5">{t('reports_sub')}</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <DateRangePicker
            presets={pickerPresets}
            value={presetKey}
            range={range}
            onChange={({ key, range: r }) => {
              setPresetKey(key);
              if (key === 'custom') setCustomRange(r);
            }}
          />
          <Button variant="secondary" onClick={() => void handleExport()} disabled={exporting}>
            {exporting ? t('reports_exporting') : t('reports_export_csv')}
          </Button>
        </div>
      </header>

      {exportError && (
        <Card padding="md" className="mb-4 border-danger-bd bg-danger-bg">
          <p className="text-[13px] text-danger">{exportError}</p>
        </Card>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <Kpi
          label={t('reports_kpi_total')}
          value={cur ? cur.total.toLocaleString(lang) : '—'}
          delta={totalDelta}
          loading={summary.isLoading}
        />
        <Kpi
          label={t('reports_kpi_delivered')}
          value={cur ? cur.delivered.toLocaleString(lang) : '—'}
          delta={deliveredDelta}
          loading={summary.isLoading}
        />
        <Kpi
          label={t('reports_kpi_failed')}
          value={cur ? cur.failed.toLocaleString(lang) : '—'}
          delta={failedDelta}
          deltaInverted
          loading={summary.isLoading}
        />
        <Kpi
          label={t('reports_kpi_inbound')}
          value={cur ? cur.inbound.toLocaleString(lang) : '—'}
          delta={inboundDelta}
          loading={summary.isLoading}
        />
        <Kpi
          label={t('reports_kpi_spend')}
          value={cur ? formatCurrency(cur.spend, lang) : '—'}
          delta={spendDelta}
          deltaInverted
          loading={summary.isLoading}
        />
      </div>

      {/* Volume chart */}
      <Card padding="lg" className="mb-6 min-h-[320px]">
        <h2 className="display text-[15px] font-semibold text-text mb-4">
          {t('reports_volume_title')}
        </h2>
        {volume.isLoading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : volumeRows.length === 0 ? (
          <div className="grid place-items-center h-[260px] text-[13px] text-text-3">
            {t('reports_no_data')}
          </div>
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={volumeRows} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                  axisLine={{ stroke: 'var(--line)' }}
                  tickLine={false}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={40}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'var(--surface)',
                    border: '1px solid var(--line)',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  name={t('reports_legend_total')}
                  stroke="var(--volt-500)"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="delivered"
                  name={t('reports_legend_delivered')}
                  stroke="var(--success)"
                  strokeWidth={1.6}
                  dot={false}
                />
                <Line
                  type="monotone"
                  dataKey="failed"
                  name={t('reports_legend_failed')}
                  stroke="var(--danger)"
                  strokeWidth={1.4}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Breakdown tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card padding="none">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[15px] font-semibold text-text">
              {t('reports_top_senders_title')}
            </h2>
          </div>
          {senders.isLoading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (senders.data?.items ?? []).length === 0 ? (
            <div className="p-8 text-center text-[13px] text-text-3">{t('reports_no_data')}</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('reports_col_sender')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_total')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_delivered')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_spend')}</th>
                </tr>
              </thead>
              <tbody>
                {senders.data?.items.map((s) => (
                  <tr key={s.sender_value} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 mono text-text">{s.sender_value}</td>
                    <td className="px-5 py-3 num text-right text-text">
                      {s.total.toLocaleString(lang)}
                    </td>
                    <td className="px-5 py-3 num text-right text-success">
                      {s.delivered.toLocaleString(lang)}
                    </td>
                    <td className="px-5 py-3 num text-right text-text-2">
                      {formatCurrency(s.spend, lang)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card padding="none">
          <div className="px-5 py-4 border-b border-line">
            <h2 className="display text-[15px] font-semibold text-text">
              {t('reports_top_campaigns_title')}
            </h2>
          </div>
          {campaigns.isLoading ? (
            <div className="p-5 space-y-2">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (campaigns.data?.items ?? []).length === 0 ? (
            <div className="p-8 text-center text-[13px] text-text-3">{t('reports_no_data')}</div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-text-4 bg-surface-2 border-b border-line">
                  <th className="px-5 py-2.5 font-medium">{t('reports_col_campaign')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_total')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_delivered')}</th>
                  <th className="px-5 py-2.5 font-medium text-right">{t('reports_col_spend')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.data?.items.map((c) => (
                  <tr key={c.campaign_id} className="border-b border-line last:border-0">
                    <td className="px-5 py-3 text-text font-medium">{c.campaign_name}</td>
                    <td className="px-5 py-3 num text-right text-text">
                      {c.total.toLocaleString(lang)}
                    </td>
                    <td className="px-5 py-3 num text-right text-success">
                      {c.delivered.toLocaleString(lang)}
                    </td>
                    <td className="px-5 py-3 num text-right text-text-2">
                      {formatCurrency(c.spend, lang)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </div>
  );
}
